'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TdeeCalculator() {
  const t = useTranslations("tools.tdeeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    feet: 5,
    inches: 10,
    pounds: 165,
    cm: 178,
    kg: 75,
    activity: 1.465,
    formula: 'mifflin' as 'mifflin' | 'katch',
    bodyfat: 20,
  });
  const [results, setResults] = useState<any>(null);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
      { maxWidth: '100%' },
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        textAlign: 'center' as const,
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.65rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    unitTabs: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      borderBottom: '2px solid #e5e7eb',
      flexWrap: 'wrap' as const,
    },
    unitTab: getResponsiveStyle(
      {
        background: 'none',
        border: 'none',
        borderBottom: '3px solid transparent',
        cursor: 'pointer',
        fontWeight: 600,
        color: '#6b7280',
        transition: 'all 0.3s ease',
      },
      { padding: '0.625rem 1rem', fontSize: '0.9rem' },
      { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    ),
    unitTabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '1.5rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: '#374151', marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.925rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        width: '100%',
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.8rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    select: getResponsiveStyle(
      {
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        width: '100%',
        cursor: 'pointer',
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.8rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    heightInputs: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.75rem',
    },
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '0.5rem',
      flexWrap: 'wrap' as const,
    },
    radioLabel: getResponsiveStyle(
      { display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#374151' },
      { fontSize: '0.9rem' },
      { fontSize: '0.925rem' },
      { fontSize: '0.95rem' }
    ),
    radio: {
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
    },
    advancedSettings: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '2px solid #e5e7eb',
    },
    settingsToggle: getResponsiveStyle(
      {
        background: 'none',
        border: 'none',
        color: '#2563eb',
        fontWeight: 600,
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    settingsContent: {
      marginTop: '1.5rem',
      display: showAdvanced ? 'block' : 'none',
    },
    activityNote: getResponsiveStyle(
      { color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' as const },
      { fontSize: '0.8rem' },
      { fontSize: '0.825rem' },
      { fontSize: '0.85rem' }
    ),
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1.5rem',
      },
      { padding: '0.875rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1.05rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    results: {
      padding: screenSize === 'mobile' ? '1.5rem' : '2rem',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      display: results ? 'block' : 'none',
    },
    resultsTitle: getResponsiveStyle(
      { color: '#1e40af', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
      { fontSize: '1.35rem' },
      { fontSize: '1.425rem' },
      { fontSize: '1.5rem' }
    ),
    resultMain: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        textAlign: 'center' as const,
        marginBottom: '1.5rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultMainLabel: getResponsiveStyle(
      { color: '#6b7280', marginBottom: '0.5rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    resultMainValue: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb', marginBottom: '0.25rem' },
      { fontSize: '2.5rem' },
      { fontSize: '2.75rem' },
      { fontSize: '3rem' }
    ),
    resultMainUnit: getResponsiveStyle(
      { color: '#6b7280' },
      { fontSize: '1.1rem' },
      { fontSize: '1.15rem' },
      { fontSize: '1.2rem' }
    ),
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : screenSize === 'tablet' ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    resultCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      },
      { padding: '1.25rem' },
      { padding: '1.375rem' },
      { padding: '1.5rem' }
    ),
    resultCardLabel: getResponsiveStyle(
      { color: '#6b7280', marginBottom: '0.5rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' }
    ),
    resultCardValue: getResponsiveStyle(
      { fontWeight: 700, color: '#374151' },
      { fontSize: '1.35rem' },
      { fontSize: '1.425rem' },
      { fontSize: '1.5rem' }
    ),
    resultInfo: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        borderLeft: '4px solid #f59e0b',
      },
      { padding: '1.25rem' },
      { padding: '1.375rem' },
      { padding: '1.5rem' }
    ),
    resultInfoTitle: getResponsiveStyle(
      { color: '#f59e0b', margin: '0 0 1rem 0' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    resultInfoText: getResponsiveStyle(
      { margin: '0.5rem 0', color: '#4b5563', lineHeight: 1.6 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      },
      { padding: '1.5rem', marginBottom: '1.5rem' },
      { padding: '2rem', marginBottom: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', marginBottom: '2.5rem', borderRadius: '1.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.5rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.2rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.125rem' }
    ),
    refList: {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    refItem: {
      marginBottom: '0.75rem',
    },
    refLink: getResponsiveStyle(
      { color: colors.primary, textDecoration: 'none', transition: 'color 0.3s ease' },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' }
    ),
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightCm: number;
    let weightKg: number;

    if (unitSystem === 'imperial') {
      heightCm = ((inputs.feet * 12) + inputs.inches) * 2.54;
      weightKg = inputs.pounds * 0.453592;
    } else {
      heightCm = inputs.cm;
      weightKg = inputs.kg;
    }

    let bmr: number;

    if (inputs.formula === 'mifflin') {
      if (inputs.gender === 'male') {
        bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) + 5;
      } else {
        bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) - 161;
      }
    } else {
      const leanMass = weightKg * (1 - inputs.bodyfat / 100);
      bmr = 370 + (21.6 * leanMass);
    }

    const tdee = bmr * inputs.activity;

    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      maintain: Math.round(tdee),
      mildLoss: Math.round(tdee - 250),
      loss: Math.round(tdee - 500),
      extremeLoss: Math.round(tdee - 1000),
      mildGain: Math.round(tdee + 250),
      gain: Math.round(tdee + 500),
      fastGain: Math.round(tdee + 1000),
    });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>{t("toolTitle")}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.unitTabs}>
              <button
                type="button"
                style={{...styles.unitTab, ...(unitSystem === 'imperial' ? styles.unitTabActive : {})}}
                onClick={() => setUnitSystem('imperial')}
              >
                {t("usUnits")}
              </button>
              <button
                type="button"
                style={{...styles.unitTab, ...(unitSystem === 'metric' ? styles.unitTabActive : {})}}
                onClick={() => setUnitSystem('metric')}
              >
                {t("metricUnits")}
              </button>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t("ageLabel")}</label>
                <input
                  type="number"
                  style={styles.input}
                  min="18"
                  max="80"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("genderLabel")}</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      style={styles.radio}
                      name="gender"
                      value="male"
                      checked={inputs.gender === 'male'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                    />
                    {t("male")}
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      style={styles.radio}
                      name="gender"
                      value="female"
                      checked={inputs.gender === 'female'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                    />
                    {t("female")}
                  </label>
                </div>
              </div>
            </div>

            {unitSystem === 'imperial' ? (
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("heightLabel")}</label>
                  <div style={styles.heightInputs}>
                    <input
                      type="number"
                      style={styles.input}
                      min="3"
                      max="8"
                      value={inputs.feet}
                      onChange={(e) => setInputs({...inputs, feet: parseInt(e.target.value)})}
                      placeholder={t("feetPlaceholder")}
                    />
                    <input
                      type="number"
                      style={styles.input}
                      min="0"
                      max="11"
                      value={inputs.inches}
                      onChange={(e) => setInputs({...inputs, inches: parseInt(e.target.value)})}
                      placeholder={t("inchesPlaceholder")}
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("weightPoundsLabel")}</label>
                  <input
                    type="number"
                    style={styles.input}
                    min="50"
                    max="500"
                    step="0.1"
                    value={inputs.pounds}
                    onChange={(e) => setInputs({...inputs, pounds: parseFloat(e.target.value)})}
                    required
                  />
                </div>
              </div>
            ) : (
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("heightCmLabel")}</label>
                  <input
                    type="number"
                    style={styles.input}
                    min="100"
                    max="250"
                    step="0.1"
                    value={inputs.cm}
                    onChange={(e) => setInputs({...inputs, cm: parseFloat(e.target.value)})}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("weightKgLabel")}</label>
                  <input
                    type="number"
                    style={styles.input}
                    min="30"
                    max="300"
                    step="0.1"
                    value={inputs.kg}
                    onChange={(e) => setInputs({...inputs, kg: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>{t("activityLabel")}</label>
              <select
                style={styles.select}
                value={inputs.activity}
                onChange={(e) => setInputs({...inputs, activity: parseFloat(e.target.value)})}
                required
              >
                <option value="1.2">{t("sedentary")}</option>
                <option value="1.375">{t("light")}</option>
                <option value="1.465">{t("moderate")}</option>
                <option value="1.55">{t("active")}</option>
                <option value="1.725">{t("veryActive")}</option>
                <option value="1.9">{t("extraActive")}</option>
              </select>
              <div style={styles.activityNote}>
                {t("activityNote")}
              </div>
            </div>

            <div style={styles.advancedSettings}>
              <button
                type="button"
                style={styles.settingsToggle}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? '-' : '+'}</span> {t("advancedSettings")}
              </button>
              <div style={styles.settingsContent}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("bmrFormulaLabel")}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        style={styles.radio}
                        name="formula"
                        value="mifflin"
                        checked={inputs.formula === 'mifflin'}
                        onChange={(e) => setInputs({...inputs, formula: e.target.value as 'mifflin' | 'katch'})}
                      />
                      {t("mifflinStJeor")}
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        style={styles.radio}
                        name="formula"
                        value="katch"
                        checked={inputs.formula === 'katch'}
                        onChange={(e) => setInputs({...inputs, formula: e.target.value as 'mifflin' | 'katch'})}
                      />
                      {t("katchMcArdle")}
                    </label>
                  </div>
                </div>

                {inputs.formula === 'katch' && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("bodyfatLabel")}</label>
                    <input
                      type="number"
                      style={styles.input}
                      min="5"
                      max="50"
                      step="0.1"
                      value={inputs.bodyfat}
                      onChange={(e) => setInputs({...inputs, bodyfat: parseFloat(e.target.value)})}
                    />
                  </div>
                )}
              </div>
            </div>

            <button type="submit" style={styles.calculateBtn}>
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("yourResults")}</h3>

              <div style={styles.resultMain}>
                <div style={styles.resultMainLabel}>{t("tdeeLabel")}</div>
                <div style={styles.resultMainValue}>{results.tdee}</div>
                <div style={styles.resultMainUnit}>{t("caloriesPerDay")}</div>
              </div>

              <div style={styles.resultGrid}>
                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("bmrLabel")}</div>
                  <div style={styles.resultCardValue}>{results.bmr} {t("cal")}</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("maintainWeight")}</div>
                  <div style={styles.resultCardValue}>{results.maintain} {t("cal")}</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("mildWeightLoss")}</div>
                  <div style={styles.resultCardValue}>{results.mildLoss} {t("cal")}</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("weightLoss")}</div>
                  <div style={styles.resultCardValue}>{results.loss} {t("cal")}</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("extremeLoss")}</div>
                  <div style={styles.resultCardValue}>{results.extremeLoss} {t("cal")}</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("mildWeightGain")}</div>
                  <div style={styles.resultCardValue}>{results.mildGain} {t("cal")}</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("weightGain")}</div>
                  <div style={styles.resultCardValue}>{results.gain} {t("cal")}</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t("fastGain")}</div>
                  <div style={styles.resultCardValue}>{results.fastGain} {t("cal")}</div>
                </div>
              </div>

              <div style={styles.resultInfo}>
                <h4 style={styles.resultInfoTitle}>{t("understandingResults")}</h4>
                <p style={styles.resultInfoText}><strong>{t("tdeeExplanation")}</strong></p>
                <p style={styles.resultInfoText}><strong>{t("bmrExplanation")}</strong></p>
                <p style={styles.resultInfoText}>{t("weightLossAdvice")}</p>
                <p style={styles.resultInfoText}>{t("safeRateAdvice")}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("step1")}</li>
          <li style={styles.listItem}>{t("step2")}</li>
          <li style={styles.listItem}>{t("step3")}</li>
          <li style={styles.listItem}>{t("step4")}</li>
          <li style={styles.listItem}>{t("step5")}</li>
          <li style={styles.listItem}>{t("step6")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>

        <h3 style={styles.h3}>{t("componentsTitle")}</h3>
        <p style={styles.paragraph}>{t("componentsIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("bmrComponent")}</strong> {t("bmrDescription")}</li>
          <li style={styles.listItem}><strong>{t("tefComponent")}</strong> {t("tefDescription")}</li>
          <li style={styles.listItem}><strong>{t("neatComponent")}</strong> {t("neatDescription")}</li>
          <li style={styles.listItem}><strong>{t("exerciseComponent")}</strong> {t("exerciseDescription")}</li>
        </ul>

        <h3 style={styles.h3}>{t("formulasTitle")}</h3>
        <p style={styles.paragraph}>{t("formulasIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("mifflinFormula")}</strong> {t("mifflinDescription")}</li>
          <li style={styles.listItem}><strong>{t("katchFormula")}</strong> {t("katchDescription")}</li>
        </ul>

        <h3 style={styles.h3}>{t("practicalUseTitle")}</h3>
        <p style={styles.paragraph}>{t("practicalUseIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("practicalTip1")}</li>
          <li style={styles.listItem}>{t("practicalTip2")}</li>
          <li style={styles.listItem}>{t("practicalTip3")}</li>
          <li style={styles.listItem}>{t("practicalTip4")}</li>
        </ul>

        <h3 style={styles.h3}>{t("limitationsTitle")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("limitation1")}</li>
          <li style={styles.listItem}>{t("limitation2")}</li>
          <li style={styles.listItem}>{t("limitation3")}</li>
          <li style={styles.listItem}>{t("limitation4")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>

        <h3 style={styles.h3}>{t("activityLevelsTitle")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("sedentaryLevel")}</strong> {t("sedentaryDescription")}</li>
          <li style={styles.listItem}><strong>{t("lightLevel")}</strong> {t("lightDescription")}</li>
          <li style={styles.listItem}><strong>{t("moderateLevel")}</strong> {t("moderateDescription")}</li>
          <li style={styles.listItem}><strong>{t("activeLevel")}</strong> {t("activeDescription")}</li>
          <li style={styles.listItem}><strong>{t("veryActiveLevel")}</strong> {t("veryActiveDescription")}</li>
          <li style={styles.listItem}><strong>{t("extraActiveLevel")}</strong> {t("extraActiveDescription")}</li>
        </ul>

        <h3 style={styles.h3}>{t("calorieTargetsTitle")}</h3>
        <p style={styles.paragraph}>{t("calorieTargetsIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("mildDeficit")}</strong> {t("mildDeficitDescription")}</li>
          <li style={styles.listItem}><strong>{t("moderateDeficit")}</strong> {t("moderateDeficitDescription")}</li>
          <li style={styles.listItem}><strong>{t("aggressiveDeficit")}</strong> {t("aggressiveDeficitDescription")}</li>
          <li style={styles.listItem}><strong>{t("mildSurplus")}</strong> {t("mildSurplusDescription")}</li>
          <li style={styles.listItem}><strong>{t("moderateSurplus")}</strong> {t("moderateSurplusDescription")}</li>
          <li style={styles.listItem}><strong>{t("aggressiveSurplus")}</strong> {t("aggressiveSurplusDescription")}</li>
        </ul>

        <h3 style={styles.h3}>{t("macronutrientsTitle")}</h3>
        <p style={styles.paragraph}>{t("macronutrientsIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("proteinMacro")}</strong> {t("proteinDescription")}</li>
          <li style={styles.listItem}><strong>{t("fatsMacro")}</strong> {t("fatsDescription")}</li>
          <li style={styles.listItem}><strong>{t("carbsMacro")}</strong> {t("carbsDescription")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq5Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq6Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq7Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq7Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq8Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq8Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.refList}>
          <li style={styles.refItem}>
            <a
              href="https://greenreliefmd.com/tdee-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t("ref1")}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.reubizzle.com/pros-cons-of-tdee-calculator-for-weight-loss/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t("ref2")}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://tdeecalculator.net"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t("ref3")}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://steelfitusa.com/blogs/health-and-wellness/calculate-tdee"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t("ref4")}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.ptpioneer.com/personal-training/certifications/study/tdee/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t("ref5")}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.naturesbest.co.uk/sports-articles/how-to-calculate-bmr-and-tdee-and-why-you-should/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t("ref6")}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://macrosinc.net/blog/what-is-tdee/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t("ref7")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}